import { EventEmitter, Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BrowserStorage } from '@app/models/browserStorage';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
@Injectable({
  providedIn: 'root',
})
export class GetLanguageService {
  currentLanguage: string = BrowserStorage.get('language') || 'en';
  languageChanged = new BehaviorSubject<string>(null);

  private readonly serviceImageTranslationFallbacks = {
    en: {
      langs: {
        recommended_image_size: 'Recommended image size',
        service_view_image_guidance_help: 'These are recommended dimensions; the landing page adapts images responsively.',
        service_view_image_guidance_one: 'Card icons and images: 256 × 256 px (square).',
        service_view_image_guidance_two: 'Main images: 1200 × 800 px. Icons: 256 × 256 px (square).',
        service_view_image_guidance_three: 'Card icons and images: 256 × 256 px (square).',
        service_view_image_guidance_four: 'Icons: 256 × 256 px (square). Content images: 800 × 600 px.',
        recommended_size_main_image: 'Recommended: 1200 × 800 px',
        recommended_size_icon: 'Recommended: 256 × 256 px (square)',
        recommended_size_content_image: 'Recommended: 800 × 600 px',
        show_button: 'Show button',
        button_link: 'Button link',
        working_process_link_hint: 'Use /working-process to open the full working process page.',
      },
    },
    es: {
      langs: {
        recommended_image_size: 'Tamaño recomendado',
        service_view_image_guidance_help: 'Son dimensiones orientativas; la landing adapta las imágenes automáticamente.',
        service_view_image_guidance_one: 'Íconos e imágenes de tarjetas: 256 × 256 px (cuadrado).',
        service_view_image_guidance_two: 'Imágenes principales: 1200 × 800 px. Íconos: 256 × 256 px (cuadrado).',
        service_view_image_guidance_three: 'Íconos e imágenes de tarjetas: 256 × 256 px (cuadrado).',
        service_view_image_guidance_four: 'Íconos: 256 × 256 px (cuadrado). Imágenes de contenido: 800 × 600 px.',
        recommended_size_main_image: 'Recomendado: 1200 × 800 px',
        recommended_size_icon: 'Recomendado: 256 × 256 px (cuadrado)',
        recommended_size_content_image: 'Recomendado: 800 × 600 px',
        show_button: 'Mostrar botón',
        button_link: 'Link del botón',
        working_process_link_hint: 'Usa /working-process para abrir la página completa del proceso de trabajo.',
      },
    },
  };
  constructor(
    private http: HttpClient,
    private fireStorage: AngularFireStorage,
    private translateService: TranslateService,
  ) { }

  async getJsonData(): Promise<boolean> {
    const jsonFileRef = this.fireStorage.refFromURL(`${environment.storageURL}shared/lang-json`);

    try {
      const fileList = await jsonFileRef.listAll().toPromise();

      for (const file of fileList.items) {
        if (file.name.endsWith('.json')) {
          const url = await file.getDownloadURL();
          const jsonData = await this.http.get(url).toPromise();
          this.translateService.setTranslation(file.name.replace('.json', ''), jsonData);
          this.translateService.use(this.currentLanguage);
        }
      }
      this.applyServiceImageTranslationFallbacks();
      return true;
    } catch (error) {
      console.error(error);
      this.applyServiceImageTranslationFallbacks();
      return false;
    }
  }

  private applyServiceImageTranslationFallbacks(): void {
    Object.entries(this.serviceImageTranslationFallbacks).forEach(([language, translations]) => {
      const currentTranslations = (this.translateService as any).store?.translations?.[language]?.langs ?? {};
      const missingTranslations = Object.entries(translations.langs).reduce((missing, [key, value]) => {
        if (!currentTranslations[key] || currentTranslations[key] === `langs.${key}`) {
          missing[key] = value;
        }
        return missing;
      }, {} as Record<string, string>);

      if (Object.keys(missingTranslations).length > 0) {
        this.translateService.setTranslation(language, { langs: missingTranslations }, true);
      }
    });
  }

  changeLanguage(language: string) {
    this.currentLanguage = language;
    BrowserStorage.set('language', language);
    this.translateService.use(language);
    this.languageChanged.next(language);
  }
}
